import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Baby, ChevronRight, Truck, Shield, ShieldCheck, Headphones, Tag } from 'lucide-react';
import { PerfumeIcon } from '../components/PerfumeIcon';
import ProductCard from '../components/ProductCard';
import { ProductCardSkeleton } from '../components/ProductCardSkeleton';
import CategorySidebar from '../components/CategorySidebar';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { productsAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { useCart } from '../context/CartContext';
import { trackAddToCart, trackViewItemList, trackSelectItem } from '../utils/analytics';
import useSEO from '../hooks/useSEO';
import api from '../services/api';

const Home = () => {
  const { toast } = useToast();
  const location = useLocation();
  const { addToGuestCart, isAuthenticated } = useCart();
  const [currentBanner, setCurrentBanner] = useState(0);
  const [banners, setBanners] = useState([]);
  const [bannersLoaded, setBannersLoaded] = useState(false);
  const [offerProducts, setOfferProducts] = useState([]);
  const [newProducts, setNewProducts] = useState([]);
  const [deadImages, setDeadImages] = useState(() => new Set());
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);

  useSEO({
    title: 'R32.ro – Marketplace Online cu peste 250.000 de produse | Fashion, Bebe & Copii, Cosmetice, Parfumuri, IT, Telefoane',
    description: 'R32.ro este un marketplace online cu peste 250.000 de produse: fashion (haine de firmă, încălțăminte, ochelari de soare), produse pentru bebeluși și copii (cărucioare, scaune auto, jucării, lenjerii și camera copilului), cosmetice și parfumuri originale, îngrijire personală, IT & calculatoare, telefoane mobile, huse și accesorii GSM, birotică și papetărie, electronice și multe altele. Livrare rapidă, plată securizată, retur în 14 zile.',
    keywords: 'produse bebelusi, produse pentru copii, articole bebelusi, magazin bebelusi online, carucioare bebelusi, scaune auto copii, jucarii copii, lenjerii si cuiburi bebelusi, perne de alaptare, lista de nastere, cosmetice, parfumuri, parfumuri originale, cosmetice online, produse cosmetice, îngrijire personală, machiaj, îngrijire păr, marketplace R32, R32.ro, magazin online România, IT și calculatoare, telefoane mobile, huse și accesorii GSM, birotică și papetărie, electronice, fashion, haine de firma, haine de firmă, incaltaminte, încălțăminte, ochelari de soare',
    canonical: typeof window !== 'undefined' ? `${window.location.origin}/` : 'https://r32.ro/',
  });

  useEffect(() => {
    // Load banners from API, fallback to mock
    const loadBanners = async () => {
      try {
        const res = await api.get('/banners?section=hero&active_only=true');
        const apiBanners = (res.data || [])
          .filter((b) => b.image_url)
          .map((b, i) => ({
            id: b.id || i + 1,
            title: b.title || '',
            subtitle: b.subtitle || '',
            image: b.image_url || '',
            link: b.button_link || '/catalog',
            buttonText: b.button_text || 'Descoperă Ofertele'
          }));
        setBanners(apiBanners);
      } catch (e) {
        setBanners([]);
      } finally {
        setBannersLoaded(true);
      }
    };
    loadBanners();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentBanner((prev) => (prev + 1) % banners.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [banners.length]);

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      // Oferte speciale și Noutăți: 8 carduri, amestecate aleator și echilibrate între TOȚI furnizorii
      // Cerem 12 și afișăm 8: dacă o poză pică, locul e completat de rezervă (fără goluri în grilă)
      const [offersResponse, newResponse, featuredResponse] = await Promise.all([
        api.get('/products/showcase', { params: { section: 'offers', limit: 12 } }),
        api.get('/products/showcase', { params: { section: 'new', limit: 12 } }),
        productsAPI.getAll({ featured: true, limit: 6, in_stock: true }),
      ]);
      setOfferProducts(offersResponse.data || []);
      setNewProducts(newResponse.data || []);
      setFeaturedProducts(featuredResponse.data || []);
    } catch (error) {
      console.error('Error loading products:', error);
      // Set empty arrays to avoid errors
      setOfferProducts([]);
      setNewProducts([]);
      setFeaturedProducts([]);
    } finally {
      setProductsLoading(false);
    }
  };

  // Cardurile cu poză moartă sunt înlocuite cu rezerve, ca să rămână mereu 8 pe secțiune
  const markImageDead = useCallback((id) => {
    setDeadImages((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
  }, []);
  const visible = (list) => list.filter((p) => !deadImages.has(p._id || p.id)).slice(0, 8);

  const handleAddToCart = async (product) => {
    try {
      if (isAuthenticated()) {
        // Logged in user - use API
        const { cartAPI } = await import('../services/api');
        await cartAPI.addItem({ productId: product._id, quantity: 1 });
      } else {
        // Guest user - use local storage
        addToGuestCart(product, 1);
      }
      
      // Track GA4 event
      trackAddToCart(product, 1);
      
      // Dispatch event to update cart count in header
      window.dispatchEvent(new Event('cartUpdated'));
      
      toast({
        title: 'Produs adăugat în coș!',
        description: `${product.name} a fost adăugat în coșul tău.`,
      });
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast({
        title: 'Eroare',
        description: error.response?.data?.detail || 'Nu s-a putut adăuga produsul în coș.',
        variant: 'destructive'
      });
    }
  };

  const handleAddToWishlist = async (product) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast({
          title: 'Trebuie să fii autentificat',
          description: 'Loghează-te pentru a adăuga produse la favorite.',
          variant: 'destructive'
        });
        return;
      }
      
      const { wishlistAPI } = await import('../services/api');
      await wishlistAPI.add({ productId: product._id });
      
      // Dispatch event to update wishlist count in header
      window.dispatchEvent(new Event('wishlistUpdated'));
      
      toast({
        title: 'Adăugat la favorite!',
        description: `${product.name} a fost adăugat la lista ta de dorințe.`,
      });
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      toast({
        title: 'Eroare',
        description: error.response?.data?.detail || 'Nu s-a putut adăuga produsul la favorite.',
        variant: 'destructive'
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-2 sm:px-4 py-4">
        <p className="mb-2 px-1 text-[11px] leading-snug text-gray-400 sm:text-xs" data-testid="site-testing-watermark">
          Site în testare — comenzile se confirmă telefonic; pot exista diferențe de stoc și preț.
        </p>
        <div className="flex gap-4 lg:gap-6">
          {/* Left Sidebar - Categories (Hidden on mobile) */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <CategorySidebar key={location.key || 'home'} />
          </aside>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Hero Banner Carousel - MOVED TO TOP */}
            <section className="relative h-48 sm:h-64 lg:h-96 overflow-hidden rounded-xl sm:rounded-2xl mb-3 sm:mb-4">
              {bannersLoaded && banners.length > 0 ? banners.map((banner, index) => (
                // montăm doar bannerul curent și pe următorul: altfel se descarcă toate imaginile mari deodată
                Math.abs(index - currentBanner) > 1 && !(currentBanner === banners.length - 1 && index === 0) ? null : (
                <div
                  key={banner.id}
                  className={`absolute inset-0 transition-opacity duration-1000 ${
                    index === currentBanner ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'
                  }`}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-green-700 to-green-500" />
                  <img
                    src={banner.image}
                    alt={banner.title}
                    loading={index === 0 ? 'eager' : 'lazy'}
                    fetchPriority={index === 0 ? 'high' : 'low'}
                    decoding={index === 0 ? 'sync' : 'async'}
                    onError={(e) => { e.target.style.display = 'none'; }}
                    className="relative h-full w-full object-cover"
                  />
                  <div className="absolute inset-0 flex items-center">
                    <div className="px-4 sm:px-8" style={{textShadow: '0 2px 12px rgba(0,0,0,0.9), 0 0 4px rgba(0,0,0,0.5)'}}>
                      <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2 sm:mb-3">{banner.title}</h1>
                      <p className="text-sm sm:text-lg lg:text-xl text-white mb-3 sm:mb-6">{banner.subtitle}</p>
                      <Link to={banner.link}>
                        <button className="bg-green-600 hover:bg-green-700 text-white rounded-xl px-4 py-3 sm:px-6 sm:py-5 text-sm sm:text-lg font-semibold inline-flex items-center shadow-lg">
                          {banner.buttonText || 'Descoperă Ofertele'}
                          <ChevronRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5" />
                        </button>
                      </Link>
                    </div>
                  </div>
                </div>
                )
              )) : (
                <div className="absolute inset-0 bg-gradient-to-r from-green-700 to-green-500 flex items-center">
                  <div className="px-4 sm:px-8">
                    <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2">R32.ro</h1>
                    <p className="text-sm sm:text-lg text-white mb-3">Simple as you</p>
                  </div>
                </div>
              )}

              {bannersLoaded && banners.length > 1 && (
              <div className="absolute bottom-2 sm:bottom-4 left-1/2 -translate-x-1/2 flex space-x-2">
                {banners.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentBanner(index)}
                    className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full transition-all ${
                      index === currentBanner ? 'bg-white w-6 sm:w-8' : 'bg-white/50'
                    }`}
                  />
                ))}
              </div>
              )}
            </section>

            {/* Features - Horizontal scroll on mobile, grid on desktop */}
            <section className="mb-4">
              {/* Mobile: Horizontal scroll - 4 buttons on 1 row - CENTERED */}
              <div className="lg:hidden flex gap-1.5 pb-2 scrollbar-hide justify-center">
                <Link to="/rca" className="min-w-0 flex-1 max-w-[100px]" data-testid="mobile-tile-rca">
                  <div className="bg-gradient-to-r from-green-600 to-green-500 text-white p-2 rounded-lg flex flex-col items-center text-center active:opacity-90">
                    <ShieldCheck className="h-8 w-8 mb-1" />
                    <div className="text-[10px] font-bold leading-tight">RCA</div>
                    <div className="text-[10px] font-bold leading-tight">Asigurări</div>
                  </div>
                </Link>
                <Link to="/bebelusi" className="min-w-0 flex-1 max-w-[100px]" data-testid="mobile-tile-bebe">
                  <div className="bg-gradient-to-r from-green-600 to-green-500 text-white p-2 rounded-lg flex flex-col items-center text-center active:opacity-90">
                    <Baby className="h-8 w-8 mb-1" />
                    <div className="text-[10px] font-bold leading-tight">Bebe</div>
                    <div className="text-[10px] font-bold leading-tight">Copii</div>
                  </div>
                </Link>
                <Link to="/catalog?category=parfumuri" className="min-w-0 flex-1 max-w-[100px]" data-testid="mobile-tile-parfumuri">
                  <div className="bg-gradient-to-r from-green-600 to-green-500 text-white p-2 rounded-lg flex flex-col items-center text-center active:opacity-90">
                    <PerfumeIcon className="h-8 w-8 mb-1" />
                    <div className="text-[10px] font-bold leading-tight">Parfumuri</div>
                    <div className="text-[10px] font-bold leading-tight">&nbsp;</div>
                  </div>
                </Link>
                <div className="bg-gradient-to-r from-green-600 to-green-500 text-white p-2 rounded-lg flex flex-col items-center text-center min-w-0 flex-1 max-w-[100px]">
                  <Tag className="h-8 w-8 mb-1" />
                  <div className="text-[10px] font-bold leading-tight">Prețuri</div>
                  <div className="text-[10px] font-bold leading-tight">Mici</div>
                </div>
              </div>

              {/* Desktop: Grid 4 columns */}
              <div className="hidden lg:grid grid-cols-4 gap-2">
                <div className="bg-gradient-to-r from-green-600 to-green-500 text-white p-4 rounded-xl flex flex-col items-center text-center">
                  <Truck className="h-10 w-10 mb-2" />
                  <div className="text-sm font-bold">Livrare Gratuită</div>
                  <div className="text-xs opacity-90">Peste 400 Lei</div>
                </div>
                <div className="bg-gradient-to-r from-green-600 to-green-500 text-white p-4 rounded-xl flex flex-col items-center text-center">
                  <Shield className="h-10 w-10 mb-2" />
                  <div className="text-sm font-bold">Garanție 2 Ani</div>
                  <div className="text-xs opacity-90">Conform legislației</div>
                </div>
                <div className="bg-gradient-to-r from-green-600 to-green-500 text-white p-4 rounded-xl flex flex-col items-center text-center">
                  <Headphones className="h-10 w-10 mb-2" />
                  <div className="text-sm font-bold">Suport 24/7</div>
                  <div className="text-xs opacity-90">Mereu disponibil</div>
                </div>
                <div className="bg-gradient-to-r from-green-600 to-green-500 text-white p-4 rounded-xl flex flex-col items-center text-center">
                  <Tag className="h-10 w-10 mb-2" />
                  <div className="text-sm font-bold">Prețuri Competitive</div>
                  <div className="text-xs opacity-90">Cele mai bune oferte</div>
                </div>
              </div>
            </section>

            {/* Offer Products */}
            <section className="py-4 sm:py-8">
              <div className="flex justify-between items-center mb-4 sm:mb-6">
                <h2 className="text-xl sm:text-2xl font-bold">Oferte Speciale</h2>
                <Link to="/catalog?filter=oferte">
                  <button className="flex min-h-[44px] items-center rounded-lg px-2 text-sm font-medium text-green-600 hover:text-green-700 active:bg-green-50 sm:text-base">
                    Vezi Toate
                    <ChevronRight className="ml-1 sm:ml-2 h-4 w-4 sm:h-5 sm:w-5" />
                  </button>
                </Link>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
                {productsLoading
                  ? Array.from({ length: 8 }).map((_, i) => <ProductCardSkeleton key={`sk-off-${i}`} />)
                  : visible(offerProducts).map((product) => (
                  <ProductCard
                    key={product._id || product.id}
                    product={product}
                    onAddToCart={handleAddToCart}
                    onAddToWishlist={handleAddToWishlist}
                    onImageDead={markImageDead}
                  />
                ))}
              </div>
            </section>

            {/* New Products */}
            <section className="py-4 sm:py-8">
              <div className="flex justify-between items-center mb-4 sm:mb-6">
                <h2 className="text-xl sm:text-2xl font-bold">Noutăți</h2>
                <Link to="/catalog?filter=noutati">
                  <button className="flex min-h-[44px] items-center rounded-lg px-2 text-sm font-medium text-green-600 hover:text-green-700 active:bg-green-50 sm:text-base">
                    Vezi Toate
                    <ChevronRight className="ml-1 sm:ml-2 h-4 w-4 sm:h-5 sm:w-5" />
                  </button>
                </Link>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
                {productsLoading
                  ? Array.from({ length: 8 }).map((_, i) => <ProductCardSkeleton key={`sk-new-${i}`} />)
                  : visible(newProducts).map((product) => (
                  <ProductCard
                    key={product._id || product.id}
                    product={product}
                    onAddToCart={handleAddToCart}
                    onAddToWishlist={handleAddToWishlist}
                    onImageDead={markImageDead}
                  />
                ))}
              </div>
            </section>

            {/* SEO: text descriptiv indexabil */}
            <section className="border-t border-gray-200 py-8 sm:py-10" data-testid="home-seo-text">
              <h2 className="mb-3 text-base font-bold text-gray-900 md:text-lg">
                Marketplace R32.ro — fashion, cosmetice, parfumuri și peste 250.000 de produse
              </h2>
              <div className="space-y-3 text-sm leading-relaxed text-gray-600">
                <p>
                  <strong>R32.ro</strong> este un marketplace online din România unde găsești
                  <strong> cosmetice</strong> și <strong>parfumuri</strong> originale, produse de
                  îngrijire personală, machiaj, îngrijire a părului și dermatocosmetice, alături de
                  produse IT &amp; calculatoare, telefoane mobile, huse și accesorii GSM, birotică
                  și papetărie, electronice, articole de pescuit și sport.
                </p>
                <p>
                  Comanzi cosmetice și parfumuri de la branduri cunoscute, cu livrare rapidă prin
                  curier în toată țara, plată securizată online sau ramburs, garanție legală de
                  conformitate 2 ani și drept de retur în 14 zile.
                </p>
                <div className="flex flex-wrap gap-2 pt-1">
                  {[
                    { label: 'Cosmetice', to: '/catalog?search=cosmetice' },
                    { label: 'Parfumuri', to: '/catalog?search=parfum' },
                    { label: 'Îngrijire personală', to: '/catalog?search=ingrijire' },
                    { label: 'Machiaj', to: '/catalog?search=machiaj' },
                    { label: 'Telefoane mobile', to: '/catalog?search=telefon' },
                    { label: 'Birotică', to: '/catalog?search=birotica' },
                  ].map((t) => (
                    <Link
                      key={t.label}
                      to={t.to}
                      className="rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:border-green-500 hover:text-green-700"
                      data-testid={`seo-tag-${t.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {t.label}
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;

